import React, { useEffect, useMemo, useState } from "react";
import {
  IconButton,
  SelectInput,
  TextButton,
  type SelectOption,
} from "../../../controls";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import type { ColumnDef, SortingState, Table } from "@tanstack/react-table";
import "./SortPopover.scss";
import SeemPopover from "../../SeemPopover/SeemPopover";
import { openManagementModesEnum } from "../../SeemPopover/Popover";

const MAX_SORT_OPTIONS = 1;

// Custom columnDef fields used across the Table design-system components but
// not present on the base @tanstack/react-table ColumnDef.
type ExtendedColumnDef<TData> = ColumnDef<TData> & {
  title?: string;
  accessorKey?: string;
  sortAttribute?: string;
  enableSorting?: boolean;
};

interface SortByField {
  sortColumn: string | null;
  desc: boolean;
  columnHeader: React.ReactNode | null;
  sortAttribute: string | null | undefined;
}

interface SortFormValues {
  sortBy: SortByField[];
}

interface ColumnSortOption {
  label: string;
  value: string | undefined;
  disabled: boolean;
  sortAttribute: string | undefined;
}

export interface SortPopoverProps<TData = unknown> {
  table: Table<TData>;
  isReadOnly?: boolean;
  tableLoading?: boolean;
  onSortChange: (sort: SortingState) => void;
  defaultSortBy?: SortingState;
  sortHelp?: React.ReactNode;
}

const SortPopover = <TData,>({
  table,
  isReadOnly,
  tableLoading,
  onSortChange,
  defaultSortBy,
  sortHelp,
}: SortPopoverProps<TData>) => {
  const [isOpen, toggleIsOpen] = useState(false);

  const methods = useForm<SortFormValues>({
    mode: "all",
    defaultValues: {
      sortBy: [],
    },
  });
  const {
    handleSubmit,
    reset,
    control,
    formState: { isValid, isDirty },
  } = methods;

  const { fields, remove, append, update } = useFieldArray({
    control,
    name: "sortBy",
  });

  const watchFieldArray = useWatch({
    control,
    name: "sortBy",
  });

  const controlledFields = useMemo(() => {
    return fields.map((field, index) => ({
      ...field,
      ...watchFieldArray[index],
    }));
  }, [fields, watchFieldArray]);

  const columnOptions = useMemo(
    () =>
      table
        ?.getAllLeafColumns()
        ?.filter(
          (column) =>
            (column.columnDef as ExtendedColumnDef<TData>)?.enableSorting
        )
        ?.map((column) => {
          const columnDef = column.columnDef as ExtendedColumnDef<TData>;
          return {
            label:
              typeof columnDef?.header === "string"
                ? columnDef?.header
                : columnDef?.title || columnDef?.accessorKey,
            value: columnDef?.accessorKey,
            disabled: !column?.getCanHide() || !!column?.getIsSorted(),
            sortAttribute: columnDef?.sortAttribute,
          } as ColumnSortOption;
        })
        ?.sort((a, b) => a.label.localeCompare(b.label)),
    [table?.getAllLeafColumns()?.length, isOpen]
  );

  const sortedColumns = useMemo(() => {
    return table
      .getAllLeafColumns()
      .filter((column) => !!column.getIsSorted())
      .sort((a, b) => {
        return a.getSortIndex() - b.getSortIndex();
      });
  }, [table.getAllLeafColumns(), isOpen]);

  const sortTitle = useMemo(() => {
    if (sortedColumns?.length > 3) {
      return `Sort (${sortedColumns?.length})`;
    }

    const sortColumnsString = sortedColumns
      ?.map((column) => {
        const columnDef = column.columnDef as ExtendedColumnDef<TData>;
        return typeof columnDef?.header === "string"
          ? columnDef?.header
          : columnDef?.title;
      })
      ?.join(", ");
    return `Sorted by ${sortColumnsString}`;
  }, [sortedColumns]);

  useEffect(() => {
    sortedColumns.forEach((column) => {
      const columnDef = column.columnDef as ExtendedColumnDef<TData>;
      if (
        !controlledFields.find(
          (field) => field?.sortColumn === columnDef?.accessorKey
        )
      ) {
        append({
          sortColumn: columnDef?.accessorKey ?? null,
          desc: column?.getIsSorted() === "desc",
          columnHeader: columnDef?.header as React.ReactNode,
          sortAttribute: columnDef?.sortAttribute ?? null,
        });
      }
    });
  }, [sortedColumns]);

  const onSubmit = (data: SortFormValues) => {
    const tableSort = data?.sortBy
      ?.filter((val) => !!val?.sortColumn)
      ?.map((column) => {
        return {
          id: column?.sortColumn as string,
          sortAttribute: column?.sortAttribute || column?.sortColumn,
          desc: column?.desc,
        };
      });

    onSortChange(tableSort as SortingState);
    table.setSorting(tableSort as SortingState);
    toggleIsOpen(false);
  };

  const resetSort = () => {
    toggleIsOpen(false);
    const clean = setTimeout(() => {
      const formSortBy = defaultSortBy?.map((sort) => ({
        ...sort,
        sortAttribute: sort.id,
        sortColumn: sort.id,
      }));
      onSortChange(formSortBy as SortingState);
      reset({
        sortBy: formSortBy as unknown as SortByField[],
      });
      table.setSorting(defaultSortBy ?? []);
    }, 20);

    return () => clearTimeout(clean);
  };

  return (
    <>
      <SeemPopover
        isOpen={isOpen}
        placement={"bottom-start"}
        body={
          <form
            className={"sort-by-form-popover"}
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className={"column-options-wrapper"}>
              {!isReadOnly && (
                <TextButton
                  label={"Reset to Default"}
                  leftIcon={"seem-icon-reset"}
                  buttonStyle={"textLink"}
                  type={"button"}
                  className={"reset-sort-button"}
                  wrapperClassName={"reset-columns-button-wrapper"}
                  onClick={resetSort}
                  data-analytics-event="user_click"
                  data-analytics-props={`{"name": "table_sort_reset_btn"}`}
                />
              )}
              <SeemPopover
                openManagementMode={openManagementModesEnum.HOVER}
                placement={"top-start"}
                className={"sort-help-popover"}
                body={isReadOnly ? sortHelp : ""}
              >
                <div className={isReadOnly ? "read-only" : ""}>
                  {controlledFields?.map((field, index) => {
                    return (
                      <div key={field.id} className={"sort-row"}>
                        <Controller
                          name={`sortBy.${index}.sortColumn`}
                          render={({ field: { onChange, value } }) => {
                            return (
                              <SelectInput
                                data-testid={`sort-row-${index}`}
                                name={`sortBy.${index}.sortColumn`}
                                menuWrapClassName="sortable-columns-menu"
                                onChange={(selectedValue: SelectOption) => {
                                  update(index, {
                                    ...field,
                                    columnHeader: selectedValue?.label,
                                    sortAttribute: (
                                      selectedValue as ColumnSortOption
                                    )?.sortAttribute,
                                  });
                                  onChange(selectedValue?.value);

                                  if (
                                    controlledFields?.length <
                                      MAX_SORT_OPTIONS &&
                                    index + 1 === controlledFields?.length
                                  ) {
                                    append({
                                      sortColumn: null,
                                      desc: false,
                                      columnHeader: null,
                                      sortAttribute: null,
                                    });
                                  }

                                  if (
                                    controlledFields.length > MAX_SORT_OPTIONS
                                  ) {
                                    remove(
                                      controlledFields
                                        ?.map((_, i) => i)
                                        ?.filter((_, i) => !!i)
                                    );
                                  }
                                }}
                                options={columnOptions}
                                setDefaultSingleOption={false}
                                value={columnOptions?.find(
                                  (item) => item.value === value
                                )}
                                placeholder={
                                  index === 0 ? "Sort by" : "Then by"
                                }
                                disabled={
                                  index > MAX_SORT_OPTIONS - 1 || isReadOnly
                                }
                              />
                            );
                          }}
                          control={control}
                          rules={{
                            required:
                              index === 0 ? "This field is required" : "",
                          }}
                        />
                        {!!field?.sortColumn && (
                          <>
                            <Controller
                              name={`sortBy.${index}.desc`}
                              control={control}
                              render={({ field: { onChange, value } }) => {
                                return (
                                  <IconButton
                                    data-testid={"sort-direction"}
                                    isIcon={
                                      !!value
                                        ? "seem-icon-arrow-down"
                                        : "seem-icon-arrow-up"
                                    }
                                    type={`button`}
                                    onClick={() => {
                                      onChange(!value);

                                      if (
                                        controlledFields.length >
                                        MAX_SORT_OPTIONS
                                      ) {
                                        remove(
                                          controlledFields
                                            ?.map((_, i) => i)
                                            ?.filter((_, i) => !!i)
                                        );
                                      }
                                    }}
                                    disabled={
                                      index > MAX_SORT_OPTIONS - 1 || isReadOnly
                                    }
                                    withTooltip={
                                      !isReadOnly && {
                                        content: !!value
                                          ? "Descending"
                                          : "Ascending",
                                      }
                                    }
                                  />
                                );
                              }}
                            />
                            <IconButton
                              data-testid={"sort-by-remove-row"}
                              isIcon={"seem-icon-bin"}
                              type={`button`}
                              onClick={() => {
                                // If there is only one row, we should reset it:
                                if (
                                  controlledFields?.length === 1 &&
                                  index === 0
                                ) {
                                  update(0, {
                                    sortColumn: null,
                                    desc: false,
                                    columnHeader: null,
                                    sortAttribute: null,
                                  });
                                } else {
                                  remove(index);
                                }
                              }}
                              withTooltip={
                                !isReadOnly && { content: "Remove Sort" }
                              }
                              disabled={
                                (controlledFields.length === 1 &&
                                  index === 0) ||
                                isReadOnly
                              }
                            />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </SeemPopover>
              {!isReadOnly && (
                <TextButton
                  data-testid={`sort-submit`}
                  label={"Submit"}
                  buttonStyle={"primary"}
                  isFullWidth
                  type={"submit"}
                  disabled={!isValid || !isDirty}
                  data-analytics-event="user_click"
                  data-analytics-props={`{"name": "table_sort_submit_btn"}`}
                />
              )}
            </div>
          </form>
        }
        onOuterAction={() => {
          toggleIsOpen(false);
          setTimeout(() => {
            reset({ sortBy: [] });
          }, 200);
        }}
      >
        <TextButton
          leftIcon={"seem-icon-table-sort"}
          className={"column-choosing-button"}
          onClick={() => toggleIsOpen((prev) => !prev)}
          data-testid={"column-choosing-button"}
          label={sortTitle}
          disabled={tableLoading}
          data-analytics-event="user_click"
          data-analytics-props={`{"name": "table_sort_btn"}`}
        />
      </SeemPopover>
    </>
  );
};

export default SortPopover;
